// src/slices/roleManagement/thunk.ts
// Async thunks for Role Management

import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  getRoleGroups,
  getRoleGroupById,
  createRoleGroup,
  updateRoleGroup,
  deleteRoleGroup,
  getModules,
  getPermissions,
  getPermissionsByModule,
  assignRolePermissions,
  getRolePageActionInputs,
  assignRolePageActionInputs,
  getPageActionInputs,
  getApiPermissions,
  getRoleApiPermissions,
  assignRoleApiPermissions,
  getRoleModules,
  assignRoleModules,
} from 'helpers/fakebackend_helper';
import {
  CreateRoleGroupRequest,
  UpdateRoleGroupRequest,
  AssignRolePermissionsRequest,
  AssignRolePageActionInputsRequest,
  AssignRoleApiPermissionsRequest,
  AssignRoleModulesRequest,
} from 'pages/RoleManagement/types';
import { createScreenText } from 'helpers/screenText';
const st = createScreenText('role_management_messages');

interface GetRoleGroupsParams {
  pageIndex?: number;
  pageSize?: number;
  search?: string;
  isActive?: boolean;
  sortBy?: string;
  sortOrder?: string;
}

// Fetch role groups list
export const fetchRoleGroups = createAsyncThunk(
  'RoleManagement/fetchRoleGroups',
  async (params: GetRoleGroupsParams = {}, { rejectWithValue }) => {
    try {
      const response = await getRoleGroups(params);
      return response.data || response;
    } catch (error: any) {
      return rejectWithValue(
        error?.message || st('co_loi_xay_ra_khi_tai_message')
      );
    }
  }
);

// Fetch single role group with full details (including permissions)
export const fetchRoleGroupDetail = createAsyncThunk(
  'RoleManagement/fetchRoleGroupDetail',
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await getRoleGroupById(id);
      return response.data || response;
    } catch (error: any) {
      return rejectWithValue(
        error?.message || st('co_loi_xay_ra_khi_tai_message_2')
      );
    }
  }
);

// Fetch modules
export const fetchModules = createAsyncThunk(
  'RoleManagement/fetchModules',
  async (_, { rejectWithValue }) => {
    try {
      const response = await getModules();
      return response.data || response;
    } catch (error: any) {
      return rejectWithValue(
        error?.message || st('co_loi_xay_ra_khi_tai_message_3')
      );
    }
  }
);

// Fetch all permissions
export const fetchPermissions = createAsyncThunk(
  'RoleManagement/fetchPermissions',
  async (_, { rejectWithValue }) => {
    try {
      const response = await getPermissions();
      return response.data || response;
    } catch (error: any) {
      return rejectWithValue(
        error?.message || st('co_loi_xay_ra_khi_tai_message_4')
      );
    }
  }
);

// Fetch permissions by module
export const fetchPermissionsByModule = createAsyncThunk(
  'RoleManagement/fetchPermissionsByModule',
  async (_moduleId: number | undefined = undefined, { rejectWithValue }) => {
    try {
      // Note: getPermissionsByModule doesn't take moduleId param
      // Server will return all permissions, filter by moduleId on client if needed
      const response = await getPermissionsByModule();
      return response.data || response;
    } catch (error: any) {
      return rejectWithValue(
        error?.message || st('co_loi_xay_ra_khi_tai_message_5')
      );
    }
  }
);

// Create new role group
export const createNewRoleGroup = createAsyncThunk(
  'RoleManagement/createRoleGroup',
  async (data: CreateRoleGroupRequest, { rejectWithValue }) => {
    try {
      const response = await createRoleGroup(data);
      return response.data || response;
    } catch (error: any) {
      return rejectWithValue(
        error?.message || st('co_loi_xay_ra_khi_tao_message')
      );
    }
  }
);

// Update existing role group
export const updateExistingRoleGroup = createAsyncThunk(
  'RoleManagement/updateRoleGroup',
  async (
    { id, data }: { id: number; data: UpdateRoleGroupRequest },
    { rejectWithValue }
  ) => {
    try {
      const response = await updateRoleGroup(id, data);
      return response.data || response;
    } catch (error: any) {
      return rejectWithValue(
        error?.message || st('co_loi_xay_ra_khi_cap_message')
      );
    }
  }
);

// Delete role group
export const deleteRoleGroupById = createAsyncThunk(
  'RoleManagement/deleteRoleGroup',
  async (id: number, { rejectWithValue }) => {
    try {
      await deleteRoleGroup(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(
        error?.message || st('co_loi_xay_ra_khi_xoa_message')
      );
    }
  }
);

// Gán permissions (theo module) cho role — popup "Cập nhật permission"
export const assignRolePermissionsForRole = createAsyncThunk(
  'RoleManagement/assignRolePermissions',
  async (
    { id, data }: { id: number; data: AssignRolePermissionsRequest },
    { rejectWithValue }
  ) => {
    try {
      const response = await assignRolePermissions(id, data);
      return response.data || response;
    } catch (error: any) {
      return rejectWithValue(
        error?.message || st('co_loi_xay_ra_khi_gan_message')
      );
    }
  }
);

// Lấy danh sách PageActionInput đã gán cho role — để set checked khi mở popup cập nhật quyền
export const fetchRolePageActionInputs = createAsyncThunk(
  'RoleManagement/fetchRolePageActionInputs',
  async (roleId: number, { rejectWithValue }) => {
    try {
      const response = await getRolePageActionInputs(roleId);
      return response.data || response;
    } catch (error: any) {
      return rejectWithValue(
        error?.message || st('co_loi_xay_ra_khi_tai_message_6')
      );
    }
  }
);

// Lấy danh sách PageActionInput theo UrlPage (của module đang chọn trong popup)
export const fetchPageActionInputsByUrlPage = createAsyncThunk(
  'RoleManagement/fetchPageActionInputsByUrlPage',
  async (urlPage: string, { rejectWithValue }) => {
    try {
      const response = await getPageActionInputs(urlPage);
      return response.data || response;
    } catch (error: any) {
      return rejectWithValue(
        error?.message || st('co_loi_xay_ra_khi_tai_message_7')
      );
    }
  }
);

// Gán PageActionInput cho role — popup "Cập nhật permission"
export const assignRolePageActionInputsForRole = createAsyncThunk(
  'RoleManagement/assignRolePageActionInputs',
  async (
    { id, data }: { id: number; data: AssignRolePageActionInputsRequest },
    { rejectWithValue }
  ) => {
    try {
      const response = await assignRolePageActionInputs(id, data);
      return response.data || response;
    } catch (error: any) {
      return rejectWithValue(
        error?.message || st('co_loi_xay_ra_khi_gan_message_2')
      );
    }
  }
);

// Lấy catalog "API permission" (quyền gọi API, quét tự động từ controller) — popup "Phân quyền API"
export const fetchApiPermissions = createAsyncThunk(
  'RoleManagement/fetchApiPermissions',
  async (_, { rejectWithValue }) => {
    try {
      const response = await getApiPermissions();
      return response.data || response;
    } catch (error: any) {
      return rejectWithValue(
        error?.message || st('co_loi_xay_ra_khi_tai_message_8')
      );
    }
  }
);

// Lấy danh sách API permission đã gán cho role — để set checked khi mở popup "Phân quyền API"
export const fetchRoleApiPermissions = createAsyncThunk(
  'RoleManagement/fetchRoleApiPermissions',
  async (roleId: number, { rejectWithValue }) => {
    try {
      const response = await getRoleApiPermissions(roleId);
      return response.data || response;
    } catch (error: any) {
      return rejectWithValue(
        error?.message || st('co_loi_xay_ra_khi_tai_message_9')
      );
    }
  }
);

// Gán API permission cho role — popup "Phân quyền API"
export const assignRoleApiPermissionsForRole = createAsyncThunk(
  'RoleManagement/assignRoleApiPermissions',
  async (
    { id, data }: { id: number; data: AssignRoleApiPermissionsRequest },
    { rejectWithValue }
  ) => {
    try {
      const response = await assignRoleApiPermissions(id, data);
      return response.data || response;
    } catch (error: any) {
      return rejectWithValue(
        error?.message || st('co_loi_xay_ra_khi_gan_message_3')
      );
    }
  }
);

// Lấy danh sách Module (menu) đã gán cho role (RoleModuleMapping) — để set checked khi mở popup
// "Cập nhật permission". Tách biệt hoàn toàn với assignRolePermissionsForRole cũ.
export const fetchRoleModules = createAsyncThunk(
  'RoleManagement/fetchRoleModules',
  async (roleId: number, { rejectWithValue }) => {
    try {
      const response = await getRoleModules(roleId);
      return response.data || response;
    } catch (error: any) {
      return rejectWithValue(
        error?.message || st('co_loi_xay_ra_khi_tai_message_10')
      );
    }
  }
);

// Gán Module (menu) cho role — popup "Cập nhật permission", quyết định role thấy được mục menu nào
export const assignRoleModulesForRole = createAsyncThunk(
  'RoleManagement/assignRoleModules',
  async (
    { id, data }: { id: number; data: AssignRoleModulesRequest },
    { rejectWithValue }
  ) => {
    try {
      const response = await assignRoleModules(id, data);
      return response.data || response;
    } catch (error: any) {
      return rejectWithValue(
        error?.message || st('co_loi_xay_ra_khi_gan_message_4')
      );
    }
  }
);
